import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Activity, ShieldCheck, ShieldAlert, Globe, Wifi, Server } from "lucide-react";

const STATE_STYLES = {
  READY: { color: "#10B981", label: "READY" },
  BUILDING: { color: "#D8AB4E", label: "BUILDING" },
  ERROR: { color: "#E55A45", label: "ERROR" },
  QUEUED: { color: "#A1A1AA", label: "QUEUED" },
};

export default function SentinelRadar({ vessel }) {
  const [deployments, setDeployments] = useState([]);
  const [domains, setDomains] = useState([]);
  const [crew, setCrew] = useState([]);
  const [tick, setTick] = useState(0);

  const load = async () => {
    const [d, dom, c] = await Promise.all([
      api.get("/sentinel/deployments"),
      api.get("/sentinel/domains"),
      api.get("/sentinel/seafarers"),
    ]);
    setDeployments(d.data);
    setDomains(dom.data);
    setCrew(c.data);
  };

  useEffect(() => {
    load();
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { load(); }, [vessel]);

  const visibleDeploys = useMemo(
    () => deployments.filter((d) => d.vessel === vessel),
    [deployments, vessel]
  );
  const vesselDomain = domains.find((d) => d.vessel === vessel);

  const errorCount = visibleDeploys.filter((d) => d.state === "ERROR").length;
  const buildingCount = visibleDeploys.filter((d) => d.state === "BUILDING").length;
  const mfaPct = crew.length ? Math.round((crew.filter((s) => s.mfa).length / crew.length) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="sentinel-radar">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono text-[#D8AB4E]">MODULE 03 // SENTINEL RADAR</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-1">Operational Vigilance</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-xl">
            Vercel deployments · domain integrity · seafarer MFA compliance — all in one cinematic sweep.
          </p>
        </div>
        <div className="flex gap-2">
          <Pulse label="DEPLOYMENTS" value={visibleDeploys.length} color="#D8AB4E" />
          <Pulse label="ERRORS" value={errorCount} color="#E55A45" />
          <Pulse label="MFA COMP." value={`${mfaPct}%`} color="#10B981" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Radar visualization */}
        <div className="glass-panel p-6 relative grain overflow-hidden">
          <div className="label-mono text-[#D8AB4E]">LIVE SWEEP</div>
          <div className="font-display text-xl mt-1 mb-4">Sentinel Array</div>
          <div className="relative aspect-square max-w-[320px] mx-auto">
            <div
              className="absolute inset-0 rounded-full border border-[#D8AB4E]/30"
              style={{
                backgroundImage:
                  "radial-gradient(circle, rgba(216,171,78,0.12) 0%, transparent 60%)",
              }}
            />
            <div className="absolute inset-[12%] rounded-full border border-[#D8AB4E]/20" />
            <div className="absolute inset-[26%] rounded-full border border-[#D8AB4E]/15" />
            <div className="absolute inset-[42%] rounded-full border border-[#D8AB4E]/10" />
            <div className="absolute top-1/2 left-0 right-0 h-px bg-[#D8AB4E]/20" />
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#D8AB4E]/20" />
            <div className="radar-sweep" />
            {/* Blips */}
            {visibleDeploys.slice(0, 8).map((d, i) => {
              const angle = (i * 47 + tick * 0.6) % 360;
              const radius = 30 + (i % 4) * 14;
              const x = 50 + radius * Math.cos((angle * Math.PI) / 180);
              const y = 50 + radius * Math.sin((angle * Math.PI) / 180);
              const c = STATE_STYLES[d.state]?.color || "#D8AB4E";
              return (
                <div
                  key={d.id}
                  className="absolute h-2 w-2 rounded-full"
                  style={{
                    left: `${x}%`, top: `${y}%`,
                    background: c,
                    boxShadow: `0 0 12px ${c}`,
                    transform: "translate(-50%, -50%)",
                  }}
                />
              );
            })}
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="font-mono-data text-[10px] tracking-[0.3em] text-[#D8AB4E]/70">{vessel.toUpperCase()}</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div className="font-mono-data text-[10px] text-zinc-400">
              <span className="block text-[#10B981] text-base">{visibleDeploys.length - errorCount - buildingCount}</span>
              READY
            </div>
            <div className="font-mono-data text-[10px] text-zinc-400">
              <span className="block text-[#D8AB4E] text-base">{buildingCount}</span>
              BUILDING
            </div>
            <div className="font-mono-data text-[10px] text-zinc-400">
              <span className="block text-[#E55A45] text-base">{errorCount}</span>
              ERROR
            </div>
          </div>
        </div>

        {/* Domain Health */}
        <div className="glass-panel p-6 relative grain lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label-mono text-[#D8AB4E]">DOMAIN INTEGRITY</div>
              <div className="font-display text-xl mt-1">Armada Endpoints</div>
            </div>
            <Globe size={16} className="text-[#D8AB4E]" />
          </div>
          <div className="space-y-3">
            {domains.map((d) => (
              <div key={d.vessel} className="grid grid-cols-12 gap-3 items-center p-3 border border-[#D8AB4E]/10 hover:border-[#D8AB4E]/30 transition-colors" data-testid={`domain-${d.vessel}`}>
                <div className="col-span-12 md:col-span-4 font-mono-data text-sm text-[#D8AB4E]">{d.domain}</div>
                <div className="col-span-6 md:col-span-2 font-mono-data text-[11px] text-zinc-300">
                  <div className="text-zinc-500 text-[9px] tracking-[0.2em]">UPTIME</div>
                  {d.uptime_30d}%
                </div>
                <div className="col-span-6 md:col-span-2 font-mono-data text-[11px] text-zinc-300">
                  <div className="text-zinc-500 text-[9px] tracking-[0.2em]">TTFB</div>
                  {d.ttfb_ms}ms
                </div>
                <div className="col-span-6 md:col-span-2 font-mono-data text-[11px] text-zinc-300">
                  <div className="text-zinc-500 text-[9px] tracking-[0.2em]">SSL</div>
                  {d.ssl_expires_in_days}d
                </div>
                <div className="col-span-6 md:col-span-2 font-mono-data text-[11px] text-[#10B981] flex items-center gap-2 justify-end">
                  <Wifi size={12} /> {d.cdn_region}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Deployments */}
      <div className="glass-panel relative grain">
        <div className="p-5 flex items-center justify-between border-b border-[#D8AB4E]/15">
          <div>
            <div className="label-mono text-[#D8AB4E]">VERCEL DEPLOYMENTS · {vessel.toUpperCase()}</div>
            <div className="font-display text-xl mt-1">Recent Bridge Pushes</div>
          </div>
          <Server size={16} className="text-[#D8AB4E]" />
        </div>
        <div className="divide-y divide-[#D8AB4E]/10">
          {visibleDeploys.map((d) => {
            const s = STATE_STYLES[d.state] || STATE_STYLES.QUEUED;
            return (
              <div key={d.id} className="p-4 grid grid-cols-12 gap-3 items-center hover:bg-[#D8AB4E]/[0.03] transition-colors" data-testid={`deployment-${d.id}`}>
                <div className="col-span-3 md:col-span-2 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: s.color, boxShadow: `0 0 8px ${s.color}` }} />
                  <span className="font-mono-data text-[10px]" style={{ color: s.color }}>{s.label}</span>
                </div>
                <div className="col-span-9 md:col-span-3 font-mono-data text-xs text-zinc-300">{d.id}</div>
                <div className="col-span-6 md:col-span-2 font-mono-data text-xs text-[#D8AB4E]">{d.branch}</div>
                <div className="col-span-6 md:col-span-2 font-mono-data text-xs text-zinc-400">{d.commit}</div>
                <div className="col-span-6 md:col-span-2 text-xs text-zinc-400">{d.author}</div>
                <div className="col-span-6 md:col-span-1 font-mono-data text-[10px] text-zinc-500 text-right">{d.duration_sec}s</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Seafarers MFA */}
      <div className="glass-panel relative grain">
        <div className="p-5 border-b border-[#D8AB4E]/15">
          <div className="label-mono text-[#D8AB4E]">SEAFARER MFA COMPLIANCE</div>
          <div className="font-display text-xl mt-1">Crew Manifest</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D8AB4E]/10">
          {crew.map((s) => (
            <motion.div
              key={s.id}
              whileHover={{ y: -2 }}
              className="bg-[#040404] p-5 hover:bg-[#0A0A0A] transition-colors"
              data-testid={`crew-${s.id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-base">{s.name}</div>
                  <div className="label-mono text-zinc-500 mt-0.5">{s.role}</div>
                </div>
                {s.mfa ? (
                  <div className="px-2 py-1 border border-[#10B981]/40 text-[#10B981] text-[9px] font-mono-data tracking-[0.18em] inline-flex items-center gap-1">
                    <ShieldCheck size={11} /> MFA
                  </div>
                ) : (
                  <div className="px-2 py-1 border border-[#C1432E]/50 text-[#E55A45] text-[9px] font-mono-data tracking-[0.18em] inline-flex items-center gap-1">
                    <ShieldAlert size={11} /> AT RISK
                  </div>
                )}
              </div>
              <div className="mt-4 flex items-center justify-between font-mono-data text-[10px] text-zinc-500">
                <span>LAST LOGIN · {s.last_login_h}h</span>
                <span>{s.device}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Pulse({ label, value, color }) {
  return (
    <div className="border border-[#D8AB4E]/20 px-3 py-2 inline-flex items-center gap-2 min-w-[120px]">
      <span className="h-1.5 w-1.5 rounded-full pulse-dot" style={{ background: color, color }} />
      <div className="flex flex-col leading-tight">
        <span className="label-mono text-zinc-500">{label}</span>
        <span className="font-mono-data text-sm" style={{ color }}>{value}</span>
      </div>
    </div>
  );
}
